import React, { useEffect, useState } from 'react'
import Layout from '../../../../../layout/Layout'
import OrdersList from 'components/organisms/OrdersList/OrdersList'
import HeaderPageSection from 'core/presentation/components/molecules/HeaderPageSection/HeaderPageSection'
import { Outlet, useNavigate } from 'react-router-dom'
import { SearchFilterValues } from 'interfaces/searchFilter'
import { RolePermissions } from 'utils/constants'
import Pagination from 'components/molecules/Pagination/Pagination'
import SearchFilter from 'components/molecules/SearchFilter/SearchFilter'
import { LoaderTransparent } from 'components/molecules/LoaderTransparent/LoaderTransparent'
import { ButtonDropdown } from 'components/molecules/ButtonDropdown/ButtonDropdown'
import AppButton from 'components/atoms/AppButton/AppButton'
import AppBox from 'components/atoms/AppBox/AppBox'
import Icon from 'assets/uploadPreAlert.png'
import ArrowIcon from 'assets/chevron-down.png'
import { usePermissions } from 'hooks/usePermissions'
import { useOrderList } from 'orders/presentation/hooks/useOrderList'
import { useOrderSummary } from 'orders/presentation/hooks/useOrderSummary'
import { OrderFilters } from 'orders/domain/repositories/IOrderRepository'
import OrdersListKPIs from 'orders/presentation/components/molecules/OrdersListKPIs/OrdersListKPIs'
import MachineOrderRestrictionBanner from 'orders/presentation/components/atoms/MachineOrderRestrictionBanner/MachineOrderRestrictionBanner'
import { DateRangePicker, type DateRange } from 'components/atoms/DatePicker/DateRangePicker'
import { isSorterMode } from 'utils/operationModeUtils'
import OrdersListStatus from 'orders/presentation/components/atoms/OrdersListStatus/OrdersListStatus'
import { ordersFilterStorage } from 'orders/presentation/utils/ordersFilterStorage'
import Message from 'components/atoms/Message/Message'
import { useFlashMessage } from 'hooks/useFlashMessage'

// Mapea el filtro de UI al filtro del listado (una sola fuente de verdad).
const toOrderFilters = (filters: SearchFilterValues): OrderFilters => ({
    created_before_than: filters.date.end ? filters.date.end.toISOString() : null,
    created_after_than: filters.date.start ? filters.date.start.toISOString() : null,
    states: filters.states.join(','),
    description: filters.search ?? null,
    classification_type_id: filters.classificationTypeId ?? null,
    without_containerization: filters.withoutContainerization ?? null,
})

const OrdersListPage = () => {
    const navigate = useNavigate()
    const { can } = usePermissions()
    const canCreateOrder = can(RolePermissions.orders.create)
    const { items, pagination, loading, error, fetchOrders, changePage, updateFilters, refetch } = useOrderList()
    const { summary, fetchSummary } = useOrderSummary()
    const flashMessage = useFlashMessage()

    // Filtro persistido (sobrevive a navegar al detalle y volver).
    const persistedFilter = React.useRef(ordersFilterStorage.load()).current
    const [filtersData, setFiltersData] = useState<OrderFilters>(
        persistedFilter
            ? toOrderFilters(persistedFilter)
            : {
                  created_before_than: null,
                  created_after_than: null,
                  states: '',
                  description: null,
                  classification_type_id: null,
                  without_containerization: null,
              }
    )

    const hasMountedRef = React.useRef(false)

    useEffect(() => {
        if (hasMountedRef.current) return
        hasMountedRef.current = true
        fetchOrders({ ...filtersData, page: 1 })
        fetchSummary()
    }, [fetchOrders, fetchSummary, filtersData])

    const handleChangePage = (page: number) => {
        changePage(page, filtersData)
    }

    const handleUpdateFilters = (filters: SearchFilterValues) => {
        const data = toOrderFilters(filters)
        setFiltersData(data)
        ordersFilterStorage.save(filters)
        updateFilters(data)
    }

    const hasMachineOrderInProcess = React.useMemo(() => {
        if (!Array.isArray(items)) return false
        return items.some(item => item.state === 'IN_PROCESS' && isSorterMode(item.operationMode ?? null))
    }, [items])

    const orderCounts = React.useMemo(() => {
        if (!Array.isArray(items)) {
            return { all: 0, inProcess: 0, finished: 0, canceled: 0 }
        }
        const inProcess = items.filter(item => item.state === 'IN_PROCESS').length
        const finished = items.filter(item => item.state === 'FINISHED').length
        const canceled = items.filter(item => item.state === 'CANCELED').length
        return {
            all: pagination.totalItems,
            inProcess,
            finished,
            canceled,
        }
    }, [items, pagination.totalItems])

    return (
        <Layout>
            {loading && <LoaderTransparent />}
            <Outlet />
            {flashMessage && <Message message={flashMessage} type="success" />}
            <HeaderPageSection
                title="Órdenes"
                subtitle="Visualizar información de órdenes y filtrar busquedas."
                showGoBack={false}
            >
                <div className="flex gap-4 items-center">
                    {canCreateOrder && (
                        <>
                            <AppButton
                                icon={<img src={Icon} alt="icon" />}
                                title="Prealertar paquetes"
                                state="ghost"
                                onClick={() => navigate('/packages/create')}
                            />

                            <ButtonDropdown
                                title={'Cargar orden'}
                                options={[
                                    {
                                        title: 'Sin criterios',
                                        path: '/orders/create/no-template',
                                    },
                                    {
                                        title: 'Con criterios',
                                        path: '/orders/create/template',
                                    },
                                ]}
                                color={'primary'}
                                size={'sm'}
                                iconRight={<img src={ArrowIcon} alt="arrow" className="w-4 h-4" />}
                            />
                        </>
                    )}
                    <DateRangePicker
                        placeholder="Seleccionar fechas"
                        align="end"
                        onChange={(range: DateRange | undefined) => {
                            if (range) {
                                handleUpdateFilters({
                                    search: filtersData.description ?? null,
                                    states: filtersData.states ? filtersData.states.split(',') : [],
                                    date: {
                                        start: range.from ?? null,
                                        end: range.to ?? null,
                                    },
                                    classificationTypeId: filtersData.classification_type_id ?? null,
                                    classificationTypeCode: '',
                                })
                            }
                        }}
                    />
                </div>
            </HeaderPageSection>

            <OrdersListKPIs
                activeOrders={summary?.activeOrders ?? 0}
                processedPackages={summary?.processedPackages ?? 0}
                openContainers={summary?.openContainers ?? 0}
                finishedOrders={summary?.finishedOrders ?? 0}
            />

            <MachineOrderRestrictionBanner show={hasMachineOrderInProcess} />

            <AppBox>
                <div className="flex flex-col gap-5">
                    <SearchFilter
                        onChange={(f) => handleUpdateFilters(f)}
                        initialValue={persistedFilter ?? undefined}
                        counts={orderCounts}
                    />
                    <div className="flex flex-col gap-3 overflow-y-auto">
                        <OrdersListStatus error={Boolean(error)} hasItems={Array.isArray(items) && items.length > 0}>
                            <OrdersList orders={items} onOrderStateChanged={refetch} />
                        </OrdersListStatus>
                    </div>
                    <div className="flex justify-center py-2">
                        <Pagination
                            totalPages={Math.ceil(
                                pagination.totalItems / pagination.itemsPerPage
                            )}
                            currentPage={pagination.page}
                            onPageChange={(page) => handleChangePage(page)}
                        />
                    </div>
                </div>
            </AppBox>
        </Layout>
    )
}

export default OrdersListPage
